import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  Textarea,
} from '@commenda/kakashi';
import { useState } from 'react';

interface IDocumentAssemblerFixedTextDialogProps {
  /** The instruction being fixed, shown so the author knows which variable they are answering. */
  instruction: string;
  isSaving?: boolean;
  onClose: () => void;
  onConfirm: (fixedText: string) => void;
}

/**
 * Asks for the words a variable should be replaced with.
 *
 * The text has to be typed: a template file carries an instruction, not a value, so there is nothing to
 * pre-fill with. Guessing one from the instruction would write that guess into every document drafted.
 */
export const DocumentAssemblerFixedTextDialog = ({
  instruction,
  isSaving,
  onClose,
  onConfirm,
}: IDocumentAssemblerFixedTextDialogProps) => {
  const [fixedText, setFixedText] = useState('');
  const trimmed = fixedText.trim();

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[520px]">
        <DialogHeader className="flex-col items-start gap-xs">
          <DialogTitle>Turn this into fixed text</DialogTitle>
          <p className="txt-small-regular text-text-secondary">
            Every document drafted from this template will read exactly what you
            write here, and nobody will be asked for it again.
          </p>
        </DialogHeader>

        <div className="flex flex-col gap-xl p-3xl">
          <FieldWrapper>
            <Label>Variable</Label>
            <p className="rounded-sm bg-bg-secondary px-md py-sm txt-extra-small-regular text-text-primary">
              {`{{${instruction}}}`}
            </p>
          </FieldWrapper>

          <FieldWrapper>
            <Label required>Text to use instead</Label>
            <Textarea
              rows={3}
              value={fixedText}
              onChange={(event) => setFixedText(event.target.value)}
              placeholder="The words this should always say"
            />
          </FieldWrapper>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(trimmed)}
            disabled={!trimmed || isSaving}
            isLoading={isSaving}
          >
            Use this text
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
